import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Clock, CreditCard, Info, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { Card, CardTitle, Empty, btn } from "@/components/common";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate, fmtMoney } from "@/lib/trip";
import { cn } from "@/lib/utils";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";

// Parse a user-entered money amount, tolerating "$", thousands commas and spaces.
// (parseFloat("2,000") === 2, which silently truncated large authorizations.)
const parseAmount = (v) => Math.round((parseFloat(String(v).replace(/[^0-9.]/g, "")) || 0) * 100) / 100;
const BRANDS = ["Visa", "Mastercard", "American Express", "Discover"];

const CardLine = ({ c, right }) => (
  <li className="flex items-center gap-3 rounded-xl border border-slate-100 px-3.5 py-3" data-testid={`card-${c.id}`}>
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-navy-50 text-navy-700"><CreditCard className="h-4 w-4" /></span>
    <span className="min-w-0 flex-1"><span className="text-sm font-medium text-navy-900">{c.brand}</span><span className="ml-1 text-sm text-slate-500">•••• {c.last4}</span></span>
    {right}
  </li>
);

export function CardsPanel({ clientId, canManage = false }) {
  const { cards = [], cardRequests = [], requestCard, addCard, removeCard, resolveCardRequest, approveCardRequest } = useDemo();
  const mine = cards.filter((c) => c.clientId === clientId);
  const reqs = cardRequests.filter((r) => r.clientId === clientId && r.status === "pending");
  const [f, setF] = useState({ brand: "Visa", last4: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (fn) => { setBusy(true); await fn(); setBusy(false); };
  const onAdd = () => {
    const last4 = (f.last4 || "").replace(/\D/g, "").slice(-4);
    if (last4.length !== 4) return toast.error("Enter the last 4 digits");
    submit(async () => { const r = canManage ? await addCard(clientId, f.brand, last4) : await requestCard({ action: "add", brand: f.brand, last4 }); if (r) setF({ brand: "Visa", last4: "" }); });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2 lg:items-start" data-testid="payment-cards-panel">
      <Card data-testid="payment-cards-add">
        <CardTitle>{canManage ? "Add a new card" : "Request a new card"}</CardTitle>
        <div className="space-y-4 p-5 sm:p-6">
          <p className="flex items-start gap-2 rounded-xl bg-slate-100 px-3.5 py-2.5 text-xs text-slate-600"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />{canManage ? "Cards live in Gateway Travel's secure vault. Only the type and last 4 digits are kept here." : "Your advisor adds the card to our secure vault after approving your request. We never store full card numbers."}</p>
          <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Card type</span>
            <select value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} className={field} data-testid="card-brand-select">{BRANDS.map((b) => <option key={b}>{b}</option>)}</select></label>
          <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Last 4 digits</span>
            <input value={f.last4} onChange={(e) => setF({ ...f, last4: e.target.value })} inputMode="numeric" maxLength={4} placeholder="1234" className={field} data-testid="card-last4-input" /></label>
          <button disabled={busy} onClick={onAdd} className={`${btn.primary} w-full`} data-testid="card-add-submit"><Plus className="h-4 w-4" />{canManage ? "Add card" : "Send request"}</button>

          {reqs.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="card-requests">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700">Pending requests</p>
              <ul className="space-y-2 text-sm text-amber-900">
                {reqs.map((r) => (
                  <li key={r.id} data-testid={`card-request-${r.id}`} className="flex flex-wrap items-center justify-between gap-2">
                    <span>{r.action === "add" ? "Add" : "Remove"} {r.brand} •••• {r.last4}</span>
                    {canManage && (
                      <span className="flex gap-1.5">
                        <button onClick={() => submit(() => approveCardRequest(r.id))} data-testid={`card-request-approve-${r.id}`} className={`${btn.primary} h-7`}><Check className="h-3.5 w-3.5" />Approve</button>
                        <button onClick={() => submit(() => resolveCardRequest(r.id))} data-testid={`card-request-resolve-${r.id}`} className={`${btn.ghost} h-7`}>Dismiss</button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Card>

      <Card data-testid="payment-cards-list">
        <CardTitle>Approved Cards</CardTitle>
        <div className="p-5 sm:p-6">
          {mine.length === 0 ? <Empty icon={CreditCard} title="No approved cards" text={canManage ? "Approve a pending request or add a card to get started." : "Your approved cards will appear here once your advisor adds them."} /> : (
            <ul className="space-y-2">
              {mine.map((c) => <CardLine key={c.id} c={c} right={
                canManage
                  ? <button onClick={() => submit(() => removeCard(c.id))} data-testid={`card-remove-${c.id}`} aria-label={`Remove ${c.brand} ending ${c.last4}`} className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  : <button onClick={() => submit(() => requestCard({ action: "remove", cardId: c.id }))} data-testid={`card-request-remove-${c.id}`} className={`${btn.ghost} h-8`}>Request Removal</button>
              } />)}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}

export function AuthorizePayment({ trip }) {
  const { cards = [], authorizePayment } = useDemo();
  const mine = cards.filter((c) => c.clientId === trip.clientId);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [cardId, setCardId] = useState("");
  const sel = mine.some((c) => c.id === cardId) ? cardId : (mine[0]?.id || "");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const amt = parseAmount(amount);
    if (amt <= 0) return toast.error("Enter an amount greater than $0");
    if (!sel) return toast.error("Choose a card");
    if (!ok) return toast.error("Please check the authorization box to continue");
    setBusy(true);
    const r = await authorizePayment(trip.id, amt, sel);
    setBusy(false);
    if (r) { setAmount(""); setOk(false); setOpen(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className={`${btn.primary} w-full`} data-testid="authorize-open-btn"><CreditCard className="h-4 w-4" />Authorize a Payment</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="authorize-payment">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-navy-900">Authorize a Payment</DialogTitle>
            <DialogDescription>Your advisor applies this to your booking — it may take 24–48 hours. We never store your full card number.</DialogDescription>
          </DialogHeader>
          {mine.length === 0 ? (
            <div className="text-sm text-slate-600" data-testid="authorize-no-card">You don't have an approved card yet. <Link to="/mytrip/profile?tab=payments" className="font-semibold text-navy-900 underline">Add one in Profile → Payments</Link> first.</div>
          ) : (
            <div className="space-y-4">
              <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Amount (USD)</span>
                <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="0.00" className={field} data-testid="authorize-amount-input" /></label>
              <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Card</span>
                <select value={sel} onChange={(e) => setCardId(e.target.value)} className={field} data-testid="authorize-card-select">{mine.map((c) => <option key={c.id} value={c.id}>{c.brand} •••• {c.last4}</option>)}</select></label>
              <label className="flex items-start gap-2.5 text-sm text-slate-700" data-testid="authorize-consent-label">
                <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="authorize-consent-checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300 text-navy-900" />
                <span>I authorize my advisor to charge the selected card for the amount above. I understand it may take 24–48 hours to be applied.</span>
              </label>
              <button disabled={busy} onClick={submit} className={`${btn.primary} w-full`} data-testid="authorize-submit-btn">{busy ? "Submitting…" : "Authorize payment"}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const ST = { PENDING: ["Pending", "bg-amber-50 text-amber-700", Clock], COMPLETE: ["Complete", "bg-emerald-50 text-emerald-700", Check] };

function AddPaymentDialog({ open, onOpenChange, onSubmit }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Manual entry");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const amt = parseAmount(amount);
    if (amt <= 0) return toast.error("Enter an amount greater than $0");
    setBusy(true);
    const r = await onSubmit({ amount: amt, method: method.trim() || "Manual entry", date: date || null });
    setBusy(false);
    if (r) { setAmount(""); setMethod("Manual entry"); setDate(""); onOpenChange(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy-900">Add a Payment</DialogTitle>
          <DialogDescription>Record a payment that was already collected — handy for past trips or offline payments. It's marked complete immediately and does not change the invoice balance.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Amount (USD)</span>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="0.00" className={field} data-testid="add-payment-amount" /></label>
          <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Method / note</span>
            <input value={method} onChange={(e) => setMethod(e.target.value)} placeholder="Manual entry" className={field} data-testid="add-payment-method" /></label>
          <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Date (optional)</span>
            <input value={date} onChange={(e) => setDate(e.target.value)} type="date" className={field} data-testid="add-payment-date" /></label>
          <button disabled={busy} onClick={submit} className={`${btn.primary} w-full`} data-testid="add-payment-submit">{busy ? "Saving…" : "Add payment"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AuthorizationsPanel({ tripId, canComplete = false }) {
  const { authorizations = [], completeAuthorization, deleteAuthorization, addManualPayment } = useDemo();
  const rows = authorizations.filter((a) => a.tripId === tripId).slice().sort((a, b) => {
    if ((a.status === "PENDING") !== (b.status === "PENDING")) return a.status === "PENDING" ? -1 : 1;
    return (b.completedAt || b.createdAt).localeCompare(a.completedAt || a.createdAt);
  });
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const confirmDelete = async () => {
    if (!delTarget) return;
    setBusy(delTarget.id);
    await deleteAuthorization(delTarget.id);
    setBusy(null);
    setDelTarget(null);
  };

  return (
    <Card data-testid="authorizations-panel">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-4 sm:px-6">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Payments &amp; authorizations</h2>
        {canComplete && <button onClick={() => setOpen(true)} data-testid="add-payment-btn" className={`${btn.secondary} h-8`}><Plus className="h-3.5 w-3.5" />Add Payment</button>}
      </div>
      {rows.length === 0 ? (
        <div className="p-5 sm:p-6"><Empty icon={CreditCard} title="No payments yet" text={canComplete ? "Authorized payments appear here as pending. Use Add payment to record a manual or past payment." : "Authorized payments will appear here as pending until your advisor applies them."} /></div>
      ) : (
        <ul className="space-y-2.5 p-5 sm:p-6">
          {rows.map((a) => {
            const [label, cls, Icon] = ST[a.status] || ST.PENDING;
            return (
              <li key={a.id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3.5 py-3" data-testid={`authorization-${a.id}`}>
                <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-full", cls)}><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-navy-900">{fmtMoney(a.amount, true)} · {a.cardLabel}</p>
                  <p className="text-xs text-slate-500">{a.status === "COMPLETE" && a.completedAt ? `Applied ${fmtDate(a.completedAt.slice(0, 10))}` : `Authorized ${fmtDate(a.createdAt.slice(0, 10))}`}</p>
                </div>
                <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", cls)} data-testid={`authorization-status-${a.id}`}>{label}</span>
                {canComplete && a.status === "PENDING" && (
                  <button disabled={busy === a.id} onClick={async () => { setBusy(a.id); await completeAuthorization(a.id); setBusy(null); }} data-testid={`authorization-complete-${a.id}`} className={`${btn.primary} h-8`}>Mark Complete</button>
                )}
                {canComplete && (
                  <button disabled={busy === a.id} onClick={() => setDelTarget(a)} data-testid={`authorization-delete-${a.id}`} aria-label={`Remove ${fmtMoney(a.amount, true)} payment`} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canComplete && <AddPaymentDialog open={open} onOpenChange={setOpen} onSubmit={(body) => addManualPayment(tripId, body)} />}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="authorization-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-navy-900">Remove this payment?</DialogTitle>
            <DialogDescription>
              {delTarget && (delTarget.status === "COMPLETE"
                ? `This removes the ${fmtMoney(delTarget.amount, true)} payment on ${delTarget.cardLabel} from the invoice and updates the balance. The client is notified in Messages.`
                : `This cancels the pending ${fmtMoney(delTarget.amount, true)} authorization on ${delTarget.cardLabel}. The client is notified in Messages.`)}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <button onClick={() => setDelTarget(null)} data-testid="authorization-delete-cancel" className={btn.secondary}>Cancel</button>
            <button disabled={!!busy} onClick={confirmDelete} data-testid="authorization-delete-confirm" className={cn(btn.primary, "bg-red-600 hover:bg-red-700")}>{busy ? "Removing…" : "Remove payment"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
